import React, { useContext, useEffect, useMemo, useState } from 'react'
import { T, Tooltip } from '@nv/react-commons/src/Components'
import { Button } from 'components/Button'
import { faArrowLeft } from '@fa-pro-light/faArrowLeft'
import { useIntl } from 'hooks/common'
import { HeaderOrderStep, LeftBlock, StyledButton, StyledFlexBlock } from '../FPLOrderCreate/styles'
import { StyledCard } from '../KontentBanner/styles'
import { Hspace } from '../../components/Hspace'
import { BulkUploadZone } from './BulkUploadZone'
import { aggregateBoxFromUploadFile, validateUploadedData } from './utils'
import { FileUploadProps } from './types'
import { UploadResult } from './UploadResult'
import { ReviewOrdersModal } from './ReviewOrderModal'
import { MMCCOrderCreationContext } from './MMCCOrderCreationContext'
import { FILE_UPLOAD_STATUS, MMCC_OC_STEP } from './constants'
import { PreviewStepInfo } from './PreviewStepInfo'
import { faBoxes } from '@fa-pro-light/faBoxes'
import { mpTrackAddedIntlOrderDetail, mpTrackUploadFile } from 'containers/FPLLogging/mixpanel'
import { flatMap, uniq } from 'lodash'
import { TRACK_ADDED_INTL_ORDER_DETAIL } from 'containers/FPLLogging/constants'
import { trackAddedIntlOrderDetail, trackUploadFile } from 'containers/FPLLogging/rum'
import {
  SERVICE_TYPE,
  SERVICE_TYPE_LABEL_IN_MIXPANEL,
  ORDER_CREATE_SOURCE,
  CREATION_METHODS,
  INPUT_METHODS
} from 'containers/FPLCommon/constants'

const initialState = {
  visibleViewDetail: false,
  numberValidItemOfValidOrder: 0,
  numberValidItemOfInvalidOrder: 0,
  numberInvalidItemOfInvalidOrder: 0
}

const FileUpload = ({ destinationCountry, onProcessOrders, onMethodChange, serviceType }: FileUploadProps) => {
  const [state, setState] = useState(initialState)
  const { visibleViewDetail } = state
  const intl = useIntl()
  const { ocState, updateOCState } = useContext(MMCCOrderCreationContext)
  const {
    fileUploadStatus,
    boxOrders,
    allOrders,
    validOrders,
    invalidOrders,
    validRows,
    invalidRows,
    fileName,
    numberOfValidItemsInValidOrders,
    numberOfInvalidItemsInInvalidOrders,
    numberOfValidItemsInInvalidOrders,
    errorsByBoxLevel,
    errorsByParcelLevel,
    customsCurrency,
    selectedService
  } = ocState

  const correctOrders = useMemo(() => {
    if (serviceType == SERVICE_TYPE.MMCCB2B || serviceType === SERVICE_TYPE.B2B_BUNDLE) {
      return validRows ? Object.values(validRows) : []
    }
    return validOrders ? Object.values(validOrders) : []
  }, [validOrders, validRows, serviceType])

  const orderTypeTitle = useMemo(() => {
    if (serviceType === SERVICE_TYPE.B2B_BUNDLE) {
      return correctOrders.length > 1 ? 'bundles' : 'singular_bundle'
    }
    return correctOrders.length > 1 ? 'boxes' : 'box'
  }, [correctOrders.length, serviceType])

  const isNoValidOrder = useMemo(() => !correctOrders.length, [correctOrders])

  const errorOrders = useMemo(() => {
    if (serviceType == SERVICE_TYPE.MMCCB2B) {
      return invalidRows ? Object.values(invalidRows) : []
    }
    return invalidOrders ? Object.values(invalidOrders) : []
  }, [invalidOrders, invalidRows, serviceType])

  const totalValuesInPreviewOrder = useMemo(() => {
    const { totalBox, totalGoodsValue, totalCartons } = aggregateBoxFromUploadFile(boxOrders, serviceType)
    return {
      totalBox,
      totalGoodsValue,
      totalCartons
    }
  }, [boxOrders, serviceType])

  useEffect(() => {
    setState(prevState => ({ ...prevState, isUploadResult: false }))
  }, [destinationCountry])

  const trackUploadedResult = (validOrdersInSummary, invalidOrdersInSummary, errorMessages) => {
    const trackingData = {
      result: 'Success',
      numberOfValidOrders: validOrdersInSummary,
      numberOfInvalidOrders: invalidOrdersInSummary,
      totalOrders: validOrdersInSummary + invalidOrdersInSummary,
      errorMessage: errorMessages.map(id => intl.formatMessage({ id })).join(', '),
      orderType: SERVICE_TYPE_LABEL_IN_MIXPANEL[serviceType]
    }
    mpTrackUploadFile(trackingData)
    trackUploadFile(trackingData)
  }

  const handleCheckOrders = data => {
    const {
      allOrders: aOrders,
      validItemisedOrders: vOrders,
      invalidItemisedOrders: inOrders,
      invalidRows: inRows,
      validRows: vRows,
      numberOfValidItemsInValidOrders: noOfValidItemsInValidOrders,
      numberOfInvalidItemsInInvalidOrders: noOfInvalidItemsInInvalidOrders,
      numberOfValidItemsInInvalidOrders: noOfValidItemsInInvalidOrders,
      errorsByBoxLevel, // Use for B2C bundle error viewer
      errorsByParcelLevel // Use for B2C bundle error viewer
    } = validateUploadedData({
      rows: data.data,
      serviceType: serviceType,
      country: destinationCountry
    })

    const allErrors = uniq(flatMap(inOrders, orderRow => flatMap(orderRow, 'errors')).filter(Boolean))

    trackUploadedResult(Object.keys(vOrders).length, Object.keys(inOrders).length, allErrors)

    const uploadedFileName = data.filename
    onProcessOrders(
      {
        allOrders: aOrders,
        validRows: vRows,
        invalidRows: inRows,
        validItemisedOrders: vOrders,
        invalidItemisedOrders: inOrders,
        numberOfValidItemsInValidOrders: noOfValidItemsInValidOrders,
        numberOfInvalidItemsInInvalidOrders: noOfInvalidItemsInInvalidOrders,
        numberOfValidItemsInInvalidOrders: noOfValidItemsInInvalidOrders,
        errorsByBoxLevel,
        errorsByParcelLevel
      },
      uploadedFileName
    )
    updateOCState({ fileUploadStatus: FILE_UPLOAD_STATUS.UPLOADED })
  }

  const handleVisibleViewDetail = () => {
    if (!errorOrders.length) return

    setState(prevState => ({ ...prevState, visibleViewDetail: !prevState.visibleViewDetail }))
  }

  const handleChangeCreationMethod = type => {
    onMethodChange(type)
    setState(prevState => ({ ...prevState }))
  }

  const handleClickBackButton = type => {
    handleChangeCreationMethod(type)
    updateOCState({ fileUploadStatus: FILE_UPLOAD_STATUS.READY_TO_UPLOAD })
  }

  const handleClickReplaceFile = type => {
    handleChangeCreationMethod(type)
    updateOCState({
      fileUploadStatus: FILE_UPLOAD_STATUS.READY_TO_UPLOAD,
      currentStep: MMCC_OC_STEP.ORDERS,
      boxOrders: []
    })
  }

  const handleSaveValidOrders = () => {
    if (isNoValidOrder) return
    updateOCState({
      fileUploadStatus: FILE_UPLOAD_STATUS.IN_PREVIEW,
      currentStep: MMCC_OC_STEP.COMMERCIAL_INVOICE
    })
    const { PROPS } = TRACK_ADDED_INTL_ORDER_DETAIL

    const trackingData = {
      [PROPS.METHOD]: CREATION_METHODS[ORDER_CREATE_SOURCE.CSV],
      [PROPS.NUMBER_OF_VALID_ORDER]: correctOrders.length,
      [PROPS.NUMBER_OF_INVALID_ORDER]: errorOrders.length,
      [PROPS.TOTAL_ORDERS]: correctOrders.length + errorOrders.length,
      [PROPS.ORDER_TYPE]: SERVICE_TYPE_LABEL_IN_MIXPANEL[serviceType]
    }
    mpTrackAddedIntlOrderDetail(trackingData)
    trackAddedIntlOrderDetail(trackingData)
  }

  const handleVisibleModal = () => {
    setState(prevState => ({ ...prevState, visibleViewDetail: !prevState.visibleViewDetail }))
  }

  const handleEditStep = () => {
    updateOCState({ fileUploadStatus: FILE_UPLOAD_STATUS.UPLOADED })
  }

  const uploadedData = useMemo(
    () => ({
      allItems: allOrders,
      invalidItems: invalidRows,
      validItems: validRows,
      errorsByParcelLevel,
      errorsByBoxLevel
    }),
    [allOrders, invalidRows, validRows, errorsByParcelLevel, errorsByBoxLevel]
  )

  const renderContinueWithValidOrders = () => {
    const compProps = {
      disabled: isNoValidOrder,
      onClick: handleSaveValidOrders
    }

    if (isNoValidOrder) {
      return (
        <Tooltip title={intl.formatMessage({ id: 'international_enable_continue_message' })} placement='topRight'>
          <StyledFlexBlock className='mt-4'>
            <StyledButton {...compProps}>
              <T id='international_continue_with_valid_orders' values={{ x: correctOrders.length }} allCaps />
              &nbsp;
              <T id={orderTypeTitle} allCaps />
            </StyledButton>
          </StyledFlexBlock>
        </Tooltip>
      )
    }
    return (
      <StyledFlexBlock className='mt-4'>
        <StyledButton {...compProps}>
          <T id='international_continue_with_valid_orders' values={{ x: correctOrders.length }} allCaps />
          &nbsp;
          <T id={orderTypeTitle} allCaps />
        </StyledButton>
      </StyledFlexBlock>
    )
  }

  return (
    <>
      {fileUploadStatus === FILE_UPLOAD_STATUS.READY_TO_UPLOAD && (
        <StyledCard data-testid='upload' hoverable={false}>
          <HeaderOrderStep>
            <T id='upload_file' />
          </HeaderOrderStep>
          {serviceType !== SERVICE_TYPE.MMCCB2C && (
            <LeftBlock>
              <Button size='small' icon={faArrowLeft} onClick={() => handleChangeCreationMethod(INPUT_METHODS.NONE)}>
                <T id='choose_input' allCaps />
              </Button>
            </LeftBlock>
          )}
          <BulkUploadZone
            serviceType={selectedService.type}
            onSave={handleCheckOrders}
            countryCode={destinationCountry}
          />
        </StyledCard>
      )}
      {fileUploadStatus === FILE_UPLOAD_STATUS.UPLOADED && (
        <StyledCard data-testid='upload' hoverable={false}>
          <HeaderOrderStep>
            <T id='international_upload_result' />
          </HeaderOrderStep>
          <LeftBlock>
            <Tooltip title={intl.formatMessage({ id: 'international_reupload_message' })}>
              <Button size='small' icon={faArrowLeft} onClick={() => handleClickBackButton(INPUT_METHODS.FILE)}>
                <T id='upload_file' allCaps />
              </Button>
            </Tooltip>
          </LeftBlock>
          <Hspace width={16} />
          <UploadResult
            validOrders={correctOrders}
            invalidOrders={errorOrders}
            numberOfValidItemsInValidOrders={numberOfValidItemsInValidOrders}
            numberOfValidItemsInInvalidOrders={numberOfValidItemsInInvalidOrders}
            numberOfInvalidItemsInInvalidOrders={numberOfInvalidItemsInInvalidOrders}
            fileName={fileName}
            serviceType={serviceType}
            onVisibleViewDetail={handleVisibleViewDetail}
            onReUpload={() => handleClickReplaceFile(INPUT_METHODS.FILE)}
          />
        </StyledCard>
      )}
      {fileUploadStatus === FILE_UPLOAD_STATUS.UPLOADED && renderContinueWithValidOrders()}
      {visibleViewDetail && (
        <ReviewOrdersModal
          dataSource={uploadedData}
          serviceType={serviceType}
          onClose={handleVisibleModal}
          country={destinationCountry}
        />
      )}
      {fileUploadStatus === FILE_UPLOAD_STATUS.IN_PREVIEW && (
        <PreviewStepInfo
          totalRecipient={totalValuesInPreviewOrder.totalBox}
          numberOfCartons={totalValuesInPreviewOrder.totalCartons}
          quantity={totalValuesInPreviewOrder.totalBox}
          unit={customsCurrency}
          creationMethod={INPUT_METHODS.FILE}
          totalGoodsValue={totalValuesInPreviewOrder.totalGoodsValue}
          titleIntlKey={
            serviceType === SERVICE_TYPE.B2B_BUNDLE ? 'total_bundles_capitalized' : 'international_total_boxes'
          }
          icon={faBoxes}
          onEditStep={handleEditStep}
        />
      )}
    </>
  )
}

export { FileUpload }
